import { z } from 'zod';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS9 — service pricing variants + per-staff pricing levels (brief 09 §1).
 *
 * Salons price by length/level/seniority. A service may carry VARIANTS (Cut → short/medium/long),
 * each with its own duration + price, and the salon defines named pricing LEVELS (junior/senior/
 * director) that a staff member sits at. `priceFrom` on a service becomes the DERIVED "from £X"
 * minimum across bookable variant/level combinations — not a stored single price. Backward compatible:
 * a service with no variants and no levels behaves exactly as today (priceFrom == its single price).
 *
 * Price is resolved DETERMINISTICALLY at/after slot choice (never gating availability): a level's
 * signed `adjustmentBps` is applied over the base price (variant price, else the service's own price),
 * unless an absolute per-(service[, variant], level) override is set, which wins.
 */

// ── Service variants ────────────────────────────────────────────────────────

const VariantName = z.string().trim().min(1).max(80);

export const ServiceVariant = z
  .object({
    id: Uuid,
    salonId: Uuid,
    serviceId: Uuid,
    name: VariantName.describe('e.g. "Short", "Medium", "Long"'),
    durationMin: z.int().min(5).max(600).describe('chair time for THIS variant'),
    pricePence: Pence.describe(
      'base VAT-inclusive price for this variant before any level adjustment',
    ),
    sortOrder: z.int().min(0).max(1000),
    active: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ServiceVariant' });
export type ServiceVariant = z.infer<typeof ServiceVariant>;

export const CreateServiceVariant = z
  .object({
    name: VariantName,
    durationMin: z.int().min(5).max(600),
    pricePence: Pence,
    sortOrder: z.int().min(0).max(1000).default(0),
  })
  .meta({ id: 'CreateServiceVariant' });
export type CreateServiceVariant = z.infer<typeof CreateServiceVariant>;

export const UpdateServiceVariant = CreateServiceVariant.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateServiceVariant' });
export type UpdateServiceVariant = z.infer<typeof UpdateServiceVariant>;

// ── Pricing levels ──────────────────────────────────────────────────────────

const LevelName = z.string().trim().min(1).max(80);

/**
 * `adjustmentBps` is SIGNED basis points applied OVER the base price: +2000 = +20% (director),
 * 0 = base (a default level), -1000 = -10% (trainee). Bounded so a level can never zero-out a price
 * or wildly inflate it by misconfiguration (matches the DB CHECK).
 */
export const PricingLevel = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: LevelName.describe('e.g. "Junior", "Senior", "Director"'),
    adjustmentBps: z
      .int()
      .min(-9000)
      .max(50000)
      .describe('signed basis points applied over the base price (+2000 = +20%)'),
    sortOrder: z.int().min(0).max(1000),
    active: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PricingLevel' });
export type PricingLevel = z.infer<typeof PricingLevel>;

export const CreatePricingLevel = z
  .object({
    name: LevelName,
    adjustmentBps: z.int().min(-9000).max(50000).default(0),
    sortOrder: z.int().min(0).max(1000).default(0),
  })
  .meta({ id: 'CreatePricingLevel' });
export type CreatePricingLevel = z.infer<typeof CreatePricingLevel>;

export const UpdatePricingLevel = CreatePricingLevel.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdatePricingLevel' });
export type UpdatePricingLevel = z.infer<typeof UpdatePricingLevel>;

// ── Staff ↔ level assignment ──────────────────────────────────────────────────

/** Assign a staff member to AT MOST ONE pricing level (the rate they charge at). */
export const AssignStaffLevel = z
  .object({
    staffId: Uuid,
    /** The level to put the staff member on, or null to clear the assignment (charge the base price). */
    levelId: Uuid.nullable(),
  })
  .meta({ id: 'AssignStaffLevel' });
export type AssignStaffLevel = z.infer<typeof AssignStaffLevel>;

export const StaffLevel = z
  .object({
    staffId: Uuid,
    levelId: Uuid.nullable(),
  })
  .meta({ id: 'StaffLevel' });
export type StaffLevel = z.infer<typeof StaffLevel>;

// ── Per-(service[, variant], level) absolute override ─────────────────────────

/**
 * An absolute price for a (service[, variant], level) combination — when set it WINS over the level's
 * bps adjustment. `variantId` null means "the bare service at this level".
 */
export const ServiceLevelPrice = z
  .object({
    id: Uuid,
    salonId: Uuid,
    serviceId: Uuid,
    variantId: Uuid.nullable(),
    levelId: Uuid,
    pricePence: Pence,
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ServiceLevelPrice' });
export type ServiceLevelPrice = z.infer<typeof ServiceLevelPrice>;

export const SetServiceLevelPrice = z
  .object({
    levelId: Uuid,
    variantId: Uuid.nullish().describe('omit/null for the bare service at this level'),
    pricePence: Pence,
  })
  .meta({ id: 'SetServiceLevelPrice' });
export type SetServiceLevelPrice = z.infer<typeof SetServiceLevelPrice>;

// ── The variant/level matrix for a service (the editor surface) ───────────────

/**
 * The full pricing matrix for one service: its variants, the salon's levels, and the absolute
 * overrides set on combinations. The console services editor renders this as a grid (rows = variants
 * incl. the bare service, columns = levels) and the resolved cell price is shown deterministically.
 */
export const ServicePricingMatrix = z
  .object({
    serviceId: Uuid,
    /** The derived "from £X" minimum across all bookable variant/level combinations. */
    priceFromPence: Pence,
    variants: z.array(ServiceVariant),
    levels: z.array(PricingLevel),
    overrides: z.array(ServiceLevelPrice),
  })
  .meta({ id: 'ServicePricingMatrix' });
export type ServicePricingMatrix = z.infer<typeof ServicePricingMatrix>;

// ── Deterministic price resolution (shown before commitment) ──────────────────

/**
 * Query a concrete resolved price for a (service, variant?, staff?) combination. A query schema must
 * NOT carry `.meta({ id })` (the receipts-entity convention): a named component used as a `querystring`
 * makes @fastify/swagger emit it as a path-level `$ref` it then cannot resolve into common params, which
 * breaks the OpenAPI emit. Plain `z.object`, exactly like FeePreviewQuery / ListPaymentsQuery.
 */
export const ResolvePriceQuery = z.object({
  serviceId: Uuid,
  variantId: Uuid.optional(),
  staffId: Uuid.optional(),
});
export type ResolvePriceQuery = z.infer<typeof ResolvePriceQuery>;

/**
 * A resolved, concrete price for a (service, variant?, staff?) combination. Deterministic and shown
 * before commitment on every booking surface (the same honesty rule as brief-07 policies). `basis`
 * tells the surface WHY the price is what it is so it can be displayed transparently.
 */
export const ResolvedPrice = z
  .object({
    serviceId: Uuid,
    variantId: Uuid.nullable(),
    staffId: Uuid.nullable(),
    levelId: Uuid.nullable().describe('the staff member’s pricing level, if any'),
    /** The base price before any level adjustment (variant price, else the service’s own price). */
    basePricePence: Pence,
    /** The concrete price the client pays for this exact combination. */
    pricePence: Pence,
    /** The variant’s duration if a variant was chosen, else the service’s own duration. */
    durationMin: z.int().min(5).max(600),
    basis: z
      .enum(['base', 'level_adjustment', 'override'])
      .describe(
        'base = no level; level_adjustment = bps applied; override = absolute per-combination price',
      ),
  })
  .meta({ id: 'ResolvedPrice' });
export type ResolvedPrice = z.infer<typeof ResolvedPrice>;
